import React, { useState } from 'react';
import { useUserSettings } from '../../context/UserSettingsContext';
import { Icons } from '../icons/Icons';
import { TERMINAL_GROUPS } from '../../constants/terminalConstants';

export const UserSettingsPage = ({ onClose }) => {
  const { settings, saveSettings, resetToDefault, updateSettings } = useUserSettings();
  const [activeSection, setActiveSection] = useState('profile');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Profilo
  const [profileData, setProfileData] = useState({
    username: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Fatturazione
  const [billingData, setBillingData] = useState({
    companyName: '',
    vatNumber: '',
    address: '',
    city: '',
    postcode: '',
    country: ''
  });

  // Terminali e Sezioni
  const [localTerminals, setLocalTerminals] = useState({});
  const [localSections, setLocalSections] = useState({});

  React.useEffect(() => {
    if (settings) {
      setLocalTerminals(settings.terminal_data || {});
      setLocalSections(settings.cable_sections || {});
      setProfileData(settings.profile_data || {});
      setBillingData(settings.billing_data || {});
    }
  }, [settings]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');
    
    try {
      const result = await saveSettings({
        ...settings,
        terminal_data: localTerminals,
        cable_sections: localSections,
        profile_data: profileData,
        billing_data: billingData
      });
      
      if (result.success) {
        setSaveMessage('Impostazioni salvate con successo!');
        setTimeout(() => setSaveMessage(''), 3000);
      } else {
        setSaveMessage('Errore nel salvataggio: ' + result.error);
      }
    } catch (error) {
      setSaveMessage('Errore nel salvataggio');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTerminalChange = (terminalCode, field, value) => {
    setLocalTerminals(prev => ({
      ...prev,
      [terminalCode]: {
        ...prev[terminalCode],
        [field]: parseFloat(value) || 0
      }
    }));
  };

  const handleSectionChange = (sectionType, index, field, value) => {
    setLocalSections(prev => ({
      ...prev,
      [sectionType]: prev[sectionType].map((sec, idx) => 
        idx === index ? { ...sec, [field]: value === '' ? '' : parseFloat(value) || 0 } : sec
      )
    }));
  };

  const groupedTerminals = {};
  Object.entries(TERMINAL_GROUPS).forEach(([group, terminals]) => {
    terminals.forEach(code => {
      if (localTerminals[code]) {
        if (!groupedTerminals[group]) groupedTerminals[group] = [];
        groupedTerminals[group].push(code);
      }
    });
  });

  const sections = [
    { id: 'profile', label: 'Profilo', icon: Icons.User },
    { id: 'billing', label: 'Fatturazione', icon: Icons.FileSpreadsheet },
    { id: 'terminals', label: 'Terminali', icon: Icons.Wire },
    { id: 'sections', label: 'Sezioni Cavi', icon: Icons.Cable }
  ];

  return (
    <div className="fixed inset-0 bg-gray-100 z-50 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="bg-black shadow-lg py-4 px-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onClose}
            className="text-white hover:text-gray-300 transition-colors"
          >
            <Icons.X className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-bold text-white">Impostazioni</h1>
        </div>
        <div className="flex items-center space-x-4">
          {saveMessage && (
            <span className={`text-sm ${saveMessage.includes('Errore') ? 'text-red-400' : 'text-green-400'}`}>
              {saveMessage}
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {isSaving ? 'Salvataggio...' : 'Salva'}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden flex">
        {/* Sidebar */}
        <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
          <nav className="py-4">
            {sections.map(section => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center space-x-3 px-6 py-3 transition-colors ${
                  activeSection === section.id
                    ? 'bg-blue-50 text-blue-600 border-r-4 border-blue-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <section.icon className="w-5 h-5" />
                <span className="font-medium">{section.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Profile Section */}
          {activeSection === 'profile' && (
            <div className="max-w-3xl mx-auto space-y-6">
              <h2 className="text-2xl font-bold text-gray-800">Profilo Utente</h2>
              
              <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome Utente
                  </label>
                  <input
                    type="text"
                    value={profileData.username}
                    onChange={(e) => setProfileData({...profileData, username: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <hr className="my-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Cambio Password</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password Attuale
                  </label>
                  <input
                    type="password"
                    value={profileData.currentPassword}
                    onChange={(e) => setProfileData({...profileData, currentPassword: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nuova Password
                  </label>
                  <input
                    type="password"
                    value={profileData.newPassword}
                    onChange={(e) => setProfileData({...profileData, newPassword: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Conferma Password
                  </label>
                  <input
                    type="password"
                    value={profileData.confirmPassword}
                    onChange={(e) => setProfileData({...profileData, confirmPassword: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Billing Section */}
          {activeSection === 'billing' && (
            <div className="max-w-3xl mx-auto space-y-6">
              <h2 className="text-2xl font-bold text-gray-800">Fatturazione</h2>
              
              <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ragione Sociale
                  </label>
                  <input
                    type="text"
                    value={billingData.companyName}
                    onChange={(e) => setBillingData({...billingData, companyName: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Partita IVA / Codice Fiscale
                  </label>
                  <input
                    type="text"
                    value={billingData.vatNumber}
                    onChange={(e) => setBillingData({...billingData, vatNumber: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Indirizzo
                  </label>
                  <input
                    type="text"
                    value={billingData.address}
                    onChange={(e) => setBillingData({...billingData, address: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Città
                    </label>
                    <input
                      type="text"
                      value={billingData.city}
                      onChange={(e) => setBillingData({...billingData, city: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CAP
                    </label>
                    <input
                      type="text"
                      value={billingData.postcode}
                      onChange={(e) => setBillingData({...billingData, postcode: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Paese
                  </label>
                  <input
                    type="text"
                    value={billingData.country}
                    onChange={(e) => setBillingData({...billingData, country: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Terminals Section */}
          {activeSection === 'terminals' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-800">Configurazione Terminali</h2>
              <p className="text-gray-600 text-sm">
                Configura i parametri per ogni tipo di terminale: len (lunghezza), peel (spelatura) e printOffset.
              </p>
              
              {Object.entries(groupedTerminals).map(([group, terminals]) => (
                <div key={group} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                  <div className="bg-gray-100 px-4 py-2 font-semibold text-gray-800 border-b border-gray-200">
                    {group}
                  </div>
                  <div className="divide-y divide-gray-200">
                    {terminals.map(code => (
                      <div key={code} className="grid grid-cols-4 gap-4 p-4 items-center hover:bg-gray-50">
                        <div className="font-medium text-gray-800">
                          <div>{code}</div>
                          <div className="text-xs text-gray-500 mt-1">{localTerminals[code]?.nome}</div>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Len (mm)</label>
                          <input
                            type="number"
                            step="0.5"
                            value={localTerminals[code]?.len || 0}
                            onChange={(e) => handleTerminalChange(code, 'len', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Peel (mm)</label>
                          <input
                            type="number"
                            step="0.5"
                            value={localTerminals[code]?.peel || 0}
                            onChange={(e) => handleTerminalChange(code, 'peel', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Print Offset</label>
                          <input
                            type="number"
                            value={localTerminals[code]?.printOffset || 0}
                            onChange={(e) => handleTerminalChange(code, 'printOffset', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Sections */}
          {activeSection === 'sections' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-800">Configurazione Sezioni Cavi</h2>
              <p className="text-gray-600 text-sm">
                Configura le sezioni dei cavi disponibili (metrico e AWG).
              </p>
              
              {localSections.SEZIONI && (
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                  <div className="bg-gray-100 px-4 py-2 font-semibold text-gray-800 border-b border-gray-200">
                    Sezioni Metrica (mm²)
                  </div>
                  <div className="divide-y divide-gray-200">
                    {localSections.SEZIONI.map((sec, idx) => (
                      <div key={idx} className="grid grid-cols-6 gap-3 p-4 items-center hover:bg-gray-50">
                        <div className="font-medium text-gray-800">Val: {sec.val}</div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Dia</label>
                          <input
                            type="number"
                            step="0.1"
                            value={sec.dia || ''}
                            onChange={(e) => handleSectionChange('SEZIONI', idx, 'dia', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Back1</label>
                          <input
                            type="number"
                            step="0.1"
                            value={sec.back1 || ''}
                            onChange={(e) => handleSectionChange('SEZIONI', idx, 'back1', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">ClampL</label>
                          <input
                            type="number"
                            step="0.5"
                            value={sec.clampGapL || ''}
                            onChange={(e) => handleSectionChange('SEZIONI', idx, 'clampGapL', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">ClampR</label>
                          <input
                            type="number"
                            step="0.5"
                            value={sec.clampGapR || ''}
                            onChange={(e) => handleSectionChange('SEZIONI', idx, 'clampGapR', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">WordSize</label>
                          <input
                            type="number"
                            value={sec.wordSize || ''}
                            onChange={(e) => handleSectionChange('SEZIONI', idx, 'wordSize', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {localSections.SEZIONI_AWG && (
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                  <div className="bg-gray-100 px-4 py-2 font-semibold text-gray-800 border-b border-gray-200">
                    Sezioni AWG
                  </div>
                  <div className="divide-y divide-gray-200">
                    {localSections.SEZIONI_AWG.map((sec, idx) => (
                      <div key={idx} className="grid grid-cols-7 gap-3 p-4 items-center hover:bg-gray-50 text-sm">
                        <div className="font-medium text-gray-800">AWG: {sec.val}</div>
                        <div className="text-gray-600 text-xs">Metric: {sec.metric}</div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Dia</label>
                          <input
                            type="number"
                            step="0.1"
                            value={sec.dia || ''}
                            onChange={(e) => handleSectionChange('SEZIONI_AWG', idx, 'dia', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Back1</label>
                          <input
                            type="number"
                            step="0.1"
                            value={sec.back1 || ''}
                            onChange={(e) => handleSectionChange('SEZIONI_AWG', idx, 'back1', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">ClampL</label>
                          <input
                            type="number"
                            step="0.5"
                            value={sec.clampGapL || ''}
                            onChange={(e) => handleSectionChange('SEZIONI_AWG', idx, 'clampGapL', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">ClampR</label>
                          <input
                            type="number"
                            step="0.5"
                            value={sec.clampGapR || ''}
                            onChange={(e) => handleSectionChange('SEZIONI_AWG', idx, 'clampGapR', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">WordSize</label>
                          <input
                            type="number"
                            value={sec.wordSize || ''}
                            onChange={(e) => handleSectionChange('SEZIONI_AWG', idx, 'wordSize', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

